import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { useEffect, useRef, useMemo } from "react";

import sevenSummitLogo from "../../assets/images/trusted-partners/seven-summit-treks-logo.png";
import karakoramLogo from "../../assets/images/trusted-partners/karakoram-club-logo.PNG";
import broadboyLogo from "../../assets/images/logo/light-logo-full.png";
import bgImage from "../../assets/images/hero-image-12-a.jpg";

export default function PartnershipSection() {
  const x = useMotionValue(0);
  const trackRef = useRef(null);
  const containerRef = useRef(null);
  const widthRef = useRef(0);

  const isInView = useInView(containerRef, { margin: "-20%" });
  const shouldReduceMotion = useReducedMotion();

  const partners = useMemo(
    () => [
      { id: "seven-summit", logo: sevenSummitLogo, name: "Seven Summit" },
      { id: "karakoram", logo: karakoramLogo, name: "Karakoram Club" },
    ],
    []
  );


  const loopPartners = useMemo(
    () =>
      [...partners, ...partners].map((p, i) => ({
        ...p,
        instanceId: `${p.id}-${i}`,
      })),
    [partners]
  );


  useEffect(() => {
    if (!trackRef.current) return;

    const updateWidth = () => {
      widthRef.current = trackRef.current.scrollWidth / 2;
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(trackRef.current);

    return () => observer.disconnect();
  }, []);


  useAnimationFrame((_, delta) => {
    if (!isInView || shouldReduceMotion) return;
    if (!widthRef.current) return;

    const moveBy = delta * 0.03;
    let next = x.get() - moveBy;

    if (Math.abs(next) >= widthRef.current) {
      next = 0;
    }

    x.set(next);
  });

  return (
    <section
      ref={containerRef}
      className="relative w-full py-20 md:py-24 lg:py-32 text-white bg-cover bg-center bg-fixed overflow-hidden border-t border-neutral-800"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#091923]/75 z-0" />

      {/* Glow */}
      <div className="absolute inset-0 flex justify-center z-0 pointer-events-none">
        <div className="w-[80%] md:w-[60%] h-full bg-[#0e202b]/30 blur-3xl opacity-40" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">

        {/* Title */}
        <div className="mb-12 md:mb-16 max-w-xl">
          <p className="text-xs sm:text-sm tracking-widest text-gray-400 mb-3 md:mb-4">
            TRUSTED PARTNERSHIP
          </p>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-semibold leading-tight mb-4 md:mb-6 lg:whitespace-nowrap">
            IN COLLABORATION
            <span className="text-gray-400 ml-2 md:ml-3">
              WITH
            </span>
          </h2>
        </div>

        {/* BELT */}
        <div className="relative overflow-hidden">

          {/* Edge fades */}
          <div className="absolute left-0 top-0 h-full w-12 md:w-24 bg-gradient-to-r from-[#202f3a] to-transparent z-10" />
          <div className="absolute right-0 top-0 h-full w-12 md:w-24 bg-gradient-to-l from-[#202f3a] to-transparent z-10" />

          {/* Light sweep */}
          <motion.div
            className="absolute top-0 left-[-30%] h-full w-[30%] bg-gradient-to-r from-transparent via-white/10 to-transparent blur-2xl z-10"
            animate={{ x: ["0%", "300%"] }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />

          {/* Track */}
          <motion.div
            ref={trackRef}
            className="flex w-max gap-12 md:gap-16 lg:gap-24"
            style={{ x }}
          >
            {loopPartners.map((partner) => (
              <div key={partner.instanceId} className="flex items-center">

                <LogoPair
                  partnerLogo={partner.logo}
                  partnerName={partner.name}
                />

                <span className="text-gray-600 text-lg md:text-xl">
                  •
                </span>

              </div>
            ))}
          </motion.div>

        </div>
      </div>
    </section>
  );
}

function LogoPair({ partnerLogo, partnerName }) {
  const ref = useRef(null);
  const scale = useMotionValue(1);

  useAnimationFrame(() => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const center = window.innerWidth / 2;
    const itemCenter = rect.left + rect.width / 2;

    const distance = Math.abs(center - itemCenter);
    const newScale = 1 + Math.max(0, 0.22 - distance / 900);

    scale.set(newScale);
  });

  return (
    <motion.div
      ref={ref}
      style={{ scale }}
      className="flex items-center gap-4 sm:gap-6 md:gap-10 whitespace-nowrap mx-6 sm:mx-10 md:mx-16 lg:mx-20 my-6 md:my-10"
    >
      {/* BroadBoy */}
      <img
        src={broadboyLogo}
        alt="BroadBoy"
        loading="lazy"
        decoding="async"
        className="h-12 sm:h-16 md:h-20 lg:h-28 object-contain opacity-90"
      />

      {/* X */}
      <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-300">
        ×
      </span>

      {/* Partner */}
      <img
        src={partnerLogo}
        alt={partnerName}
        loading="lazy"
        decoding="async"
        className="h-12 sm:h-16 md:h-20 lg:h-28 object-contain opacity-90"
      />
    </motion.div>
  );
}